import React from "react";
import { useQuery, useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { Form } from "react-final-form";
import { OnChange } from "react-final-form-listeners";
import { Remarkable } from "remarkable";
import { TextField, SelectField } from "@syos-music/form";

import { Grid, MenuItem } from "@mui/material";
import { LoadingButton } from "@mui/lab";

import Layout from "components/common/Layout";
import signatureTemplate from "components/user/signatureTemplate";
import MarkdownField from "components/form/MarkdownField";
import MailPreview from "components/common/MailPreview";

const md = new Remarkable({ html: true });

const QUERY = gql`
  query {
    templates {
      id
      name
      subject {
        en
        fr
      }
      content {
        en
        fr
      }
    }

    user {
      id
      ...signatureTemplate_user
    }
  }

  ${signatureTemplate.fragments.user}
`;

const MUTATION = gql`
  mutation ($orderId: ID!, $toUserId: ID!, $subject: String!, $body: String!) {
    SendmailOrder(
      orderId: $orderId
      toUserId: $toUserId
      subject: $subject
      body: $body
    ) {
      order {
        id
      }
    }
  }
`;

export default function OrderActionMail({ order, onSubmitted }) {
  const mailPreviewRef = React.useRef();

  const { data, loading } = useQuery(QUERY);

  const [sendMail] = useMutation(MUTATION);

  const users = [order.client, order.user].filter((u) => !!u);

  if (loading) return <Layout.Loading />;

  return (
    <Form
      onSubmit={({ toUserId, subject, mdBody }) => {
        // Mail content
        const body = md.render(mdBody);

        // Signature
        const signature = signatureTemplate(data.user);

        return sendMail({
          variables: {
            orderId: order.id,
            toUserId,
            subject,
            body: body + signature,
          },
        })
          .then(() => {
            onSubmitted();
          })
          .catch(() => true);
      }}
      initialValues={{
        toUserId: users[0] && users[0].id,
      }}
      keepDirtyOnReinitialize
    >
      {({ handleSubmit, submitting, pristine, values, form }) => (
        <form onSubmit={handleSubmit} noValidate autoComplete="off">
          <Grid container justifyContent="flex-end">
            <Grid item xs={12}>
              <SelectField name="template" label="Template">
                {data?.templates.map((template) => (
                  <MenuItem key={template.id} value={template}>
                    {template.name}
                  </MenuItem>
                ))}
              </SelectField>

              <OnChange name="template">
                {(template) => {
                  if (!template) return;

                  const ref = mailPreviewRef.current;
                  const { subject, mdBody } = ref.compileTemplate(template);

                  form.change("subject", subject);
                  form.change("mdBody", mdBody);
                  form.change("template", null);
                }}
              </OnChange>
            </Grid>

            <Grid item xs={12}>
              <SelectField name="toUserId" label="Destinataire" required>
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.profile.fullName} &lt;{user.profile.email}&gt;
                  </MenuItem>
                ))}
              </SelectField>
            </Grid>

            <Grid item xs={12}>
              <TextField name="subject" label="Sujet" required />
            </Grid>

            <Grid item xs={12}>
              <MarkdownField name="mdBody" label="Message" required />
            </Grid>

            <Grid item xs={12}>
              <MailPreview
                ref={mailPreviewRef}
                user={data.user}
                order={order}
                subject={values.subject}
                mdBody={values.mdBody}
                withSignature
              />
            </Grid>

            <Grid item>
              <LoadingButton
                type="submit"
                loading={submitting}
                disabled={pristine}
              >
                Envoyer
              </LoadingButton>
            </Grid>
          </Grid>
        </form>
      )}
    </Form>
  );
}

OrderActionMail.fragments = {
  order: gql`
    fragment OrderActions_Mail_order on Order {
      id

      user {
        id

        profile {
          email
          fullName
        }
      }

      ...MailPreview_order
    }

    ${MailPreview.fragments.order}
  `,
};
